/**
 * Portal do cliente (endereço /portal/<escritório>). Separado do sistema do escritório:
 * entrada por código no e-mail, token próprio (só vale no portal) e só o que o escritório liberou.
 */
import { useEffect, useRef, useState } from "react";

interface Info { office: string; color: string | null; enabled: boolean }
interface Item { kind: "processo" | "caso"; id: string; number: string; title: string | null; organ: string | null; status: string | null; lastDate: string | null }
interface View {
  id: string; number: string; title: string | null; organ: string; level: string; status: string | null; nextSteps: string | null; cutoffAt: string | null;
  timeline: Array<{ kind: "andamento" | "movimentacao"; id: string; date: string; title: string; body: string | null }>;
  documents: Array<{ id: string; name: string; mimeType: string; size: number; date: string; sentByClient: boolean }>;
}

const fmt = (d: string | null | undefined) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "—");
const kb = (n: number) => (n < 1048576 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1048576).toFixed(1)} MB`);

export function PortalApp({ slug }: { slug: string }): JSX.Element {
  const base = `/api/v1/portal/${encodeURIComponent(slug)}`;
  const KEY = `vg:portal:${slug}`;
  const [token, setToken] = useState<string | null>(() => { try { return sessionStorage.getItem(KEY); } catch { return null; } });
  const [info, setInfo] = useState<Info | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { void fetch(base).then((r) => (r.ok ? r.json() : Promise.reject(new Error("Portal não encontrado.")))).then(setInfo).catch((e: Error) => setErr(e.message)); }, [base]);
  useEffect(() => { if (info) document.title = `Portal do cliente — ${info.office}`; }, [info]);

  const call = async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
    const r = await fetch(`${base}${path}`, { ...init, headers: { ...(init.body && !(init.body instanceof FormData) ? { "content-type": "application/json" } : {}), ...(token ? { authorization: `Bearer ${token}` } : {}), ...(init.headers ?? {}) } });
    if (r.status === 401) { logout(); throw new Error("Sua sessão terminou. Entre de novo."); }
    if (!r.ok) { const j = (await r.json().catch(() => ({}))) as { message?: string }; throw new Error(j.message ?? `Erro ${r.status}`); }
    return (r.headers.get("content-type") ?? "").includes("json") ? ((await r.json()) as T) : (undefined as T);
  };
  const login = (t: string) => { setToken(t); try { sessionStorage.setItem(KEY, t); } catch { /* sem armazenamento */ } };
  const logout = () => { setToken(null); try { sessionStorage.removeItem(KEY); } catch { /* idem */ } };

  const color = info?.color ?? "#1F3350";
  return (
    <div className="portal" style={{ ["--portal" as string]: color }}>
      <header className="portal-top">
        <div className="portal-brand">{info?.office ?? "Portal do cliente"}</div>
        <div className="muted small">Portal do cliente</div>
        <span className="spacer" />
        {token && <button className="btn small" onClick={logout}>Sair</button>}
      </header>
      <main className="portal-main">
        {err ? <div className="banner-bad">{err}</div>
          : !info ? <div className="muted">Carregando…</div>
          : !info.enabled ? <div className="card pad">O portal do cliente não está disponível no momento. Fale com o escritório.</div>
          : !token ? <PortalLogin call={call} onLogin={login} />
          : <PortalHome call={call} base={base} token={token} />}
      </main>
      <footer className="portal-foot muted small">As informações exibidas são as liberadas pelo escritório. Em caso de dúvida, fale com o seu advogado.</footer>
    </div>
  );
}

type Call = <T>(path: string, init?: RequestInit) => Promise<T>;

function PortalLogin({ call, onLogin }: { call: Call; onLogin: (t: string) => void }): JSX.Element {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const send = async () => {
    setBusy(true); setMsg(null);
    try { await call("/entrar", { method: "POST", body: JSON.stringify({ email }) }); setStep("code"); } catch (e) { setMsg((e as Error).message); } finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true); setMsg(null);
    try { const r = await call<{ token: string }>("/confirmar", { method: "POST", body: JSON.stringify({ email, code }) }); onLogin(r.token); } catch (e) { setMsg((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="card pad portal-login">
      <h2 style={{ marginTop: 0 }}>Acompanhe seus processos</h2>
      {step === "email" ? <>
        <p className="muted">Informe o e-mail cadastrado no escritório. Vamos enviar um código de acesso.</p>
        <input className="input" type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="seu@email.com" onKeyDown={(e) => e.key === "Enter" && email && void send()} />
        <button className="btn primary" style={{ marginTop: 12, width: "100%" }} disabled={!email || busy} onClick={() => void send()}>{busy ? "Enviando…" : "Receber código"}</button>
      </> : <>
        <p className="muted">Se este e-mail tiver acesso, você recebeu um código de 6 dígitos (vale 10 minutos). Confira também o lixo eletrônico.</p>
        <input className="input mono" inputMode="numeric" autoFocus maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" onKeyDown={(e) => e.key === "Enter" && code.length === 6 && void confirm()} />
        <button className="btn primary" style={{ marginTop: 12, width: "100%" }} disabled={code.length !== 6 || busy} onClick={() => void confirm()}>{busy ? "Conferindo…" : "Entrar"}</button>
        <button className="link-btn small" style={{ marginTop: 8 }} onClick={() => { setStep("email"); setCode(""); }}>Usar outro e-mail ou pedir novo código</button>
      </>}
      {msg && <div className="banner-bad" style={{ marginTop: 12 }}>{msg}</div>}
    </div>
  );
}

function PortalHome({ call, base, token }: { call: Call; base: string; token: string }): JSX.Element {
  const [data, setData] = useState<{ name: string; clients: string[]; items: Item[] } | null>(null);
  const [open, setOpen] = useState<Item | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { void call<{ name: string; clients: string[]; items: Item[] }>("/processos").then(setData).catch((e: Error) => setErr(e.message)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <div className="banner-bad">{err}</div>;
  if (!data) return <div className="muted">Carregando…</div>;
  if (open) return <PortalDetail item={open} call={call} base={base} token={token} onBack={() => setOpen(null)} />;
  return (
    <div className="stack gap-12">
      <div><h2 style={{ margin: 0 }}>Olá, {data.name.split(" ")[0]}</h2><div className="muted small">{data.clients.join(" · ")}</div></div>
      {!data.items.length ? <div className="card pad muted">Nenhum processo disponível no portal ainda.</div> : data.items.map((it) => (
        <button key={`${it.kind}${it.id}`} className="card pad portal-item" onClick={() => setOpen(it)}>
          <div className="row nowrap" style={{ justifyContent: "space-between", gap: 8 }}>
            <b className={it.kind === "processo" ? "mono" : ""}>{it.number}</b>
            {it.lastDate && <span className="muted small">atualizado em {fmt(it.lastDate)}</span>}
          </div>
          {it.title && <div className="small">{it.title}{it.organ ? ` · ${it.organ}` : ""}</div>}
          {it.status && <div className="portal-status">{it.status}</div>}
        </button>
      ))}
    </div>
  );
}

function PortalDetail({ item, call, base, token, onBack }: { item: Item; call: Call; base: string; token: string; onBack: () => void }): JSX.Element {
  const [v, setV] = useState<View | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const load = () => void call<View>(`/${item.kind}/${item.id}`).then(setV).catch((e: Error) => setErr(e.message));
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps
  const download = async (d: View["documents"][number]) => {
    const r = await fetch(`${base}/${item.kind}/${item.id}/documentos/${d.id}`, { headers: { authorization: `Bearer ${token}` } });
    if (!r.ok) { setErr("Não foi possível baixar o documento."); return; }
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement("a"); a.href = url; a.download = d.name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };
  const upload = async (f: File) => {
    setSending(true); setSent(null); setErr(null);
    try {
      const fd = new FormData(); fd.append("file", f, f.name);
      await call(`/${item.kind}/${item.id}/documentos`, { method: "POST", body: fd });
      setSent(`“${f.name}” enviado. O escritório vai conferir.`); load();
    } catch (e) { setErr((e as Error).message); } finally { setSending(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <div className="stack gap-12">
      <button className="link-btn" onClick={onBack}>← Voltar</button>
      {err && <div className="banner-bad">{err}</div>}
      {!v ? <div className="muted">Carregando…</div> : <>
        <div className="card pad">
          <div className="muted small">{item.kind === "processo" ? "Processo" : "Caso"}</div>
          <h2 style={{ margin: "2px 0" }} className={item.kind === "processo" ? "mono" : ""}>{v.number}</h2>
          {v.title && <div>{v.title}{v.organ ? ` · ${v.organ}` : ""}</div>}
          {v.status && <div className="portal-status" style={{ marginTop: 10 }}><b>Situação:</b> {v.status}</div>}
          {v.nextSteps && <div style={{ marginTop: 8 }}><b>Próximos passos:</b> {v.nextSteps}</div>}
          {v.cutoffAt && <div className="muted small" style={{ marginTop: 8 }}>Informações até {fmt(v.cutoffAt)}.</div>}
        </div>
        {v.level !== "status" && (
          <div className="card pad">
            <h3 style={{ marginTop: 0 }}>Andamentos</h3>
            {!v.timeline.length ? <div className="muted">Nenhum andamento por enquanto.</div> : (
              <ul className="portal-timeline">{v.timeline.map((t) => (
                <li key={t.kind + t.id}><span className="muted small">{fmt(t.date)}</span><div><b>{t.title}</b>{t.body && <div style={{ whiteSpace: "pre-wrap" }}>{t.body}</div>}</div></li>
              ))}</ul>
            )}
          </div>
        )}
        <div className="card pad">
          <div className="row nowrap" style={{ justifyContent: "space-between" }}>
            <h3 style={{ margin: 0 }}>Documentos</h3>
            <label className={`btn small ${sending ? "disabled" : ""}`}>
              {sending ? "Enviando…" : "Enviar documento"}
              <input ref={fileRef} type="file" hidden disabled={sending} onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} />
            </label>
          </div>
          {sent && <div className="banner-ok" style={{ marginTop: 8 }}>{sent}</div>}
          {!v.documents.length ? <div className="muted" style={{ marginTop: 8 }}>Nenhum documento disponível.</div> : (
            <ul className="portal-docs">{v.documents.map((d) => (
              <li key={d.id}><button className="link-btn" onClick={() => void download(d)}>{d.name}</button> <span className="muted small">{fmt(d.date)} · {kb(d.size)}{d.sentByClient ? " · enviado por você" : ""}</span></li>
            ))}</ul>
          )}
        </div>
      </>}
    </div>
  );
}
